import type { ReadonlySignal } from '@preact/signals-react';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import type { WidgetLocation } from 'src/types/WidgetLocation';
import { z } from 'zod';

export const widgetIdSchema = z.string().brand<'WidgetId'>();
export type WidgetId = z.infer<typeof widgetIdSchema>;

export const widgetContainerIdSchema = z.string().brand<'WidgetContainerId'>();
export type WidgetContainerId = z.infer<typeof widgetContainerIdSchema>;

export const windowIdSchema = z.string().brand<'WindowId'>();
export type WindowId = z.infer<typeof windowIdSchema>;

/**
 * Closure that returns the initial persisted state for a widget instance.
 *
 * Synchronous by contract: the caller of `WidgetContainer.addInitialWidget` must
 * already hold the state (web reads its in-memory ledger; native windows
 * receive it in the window-init payload or the instantiate IPC message), so
 * widget construction never awaits a transport. This keeps registration —
 * and entitlement-driven placeholder/real swaps — a single synchronous
 * operation. See ADR 0008 §Widget initialization via `GetInitialData`.
 */
export type GetInitialData<T> = () => T;

export interface SelfOwnedWidget<TState> {
    toJSON(): TState;
    readonly dirty: ReadonlySignal<boolean>;
    markClean(): void;
    /** Optional synchronous, in-place response to a save; never initial hydration or workspace switching. */
    onSaveAcknowledged?(context: { submitted: TState; saved: TState }): void;
}

export interface SelfOwnedWidgetClass<
    TState,
    TModel extends SelfOwnedWidget<TState>,
    // `any` preserves compatibility for consumers that only name the runtime
    // state. Concrete class schemas still retain and enforce their wire input.
    TWireState = any,
    TInitialState = any,
    TWireInitialState = any,
> {
    /** Bidirectional schema: decode persisted wire state and encode live model state. */
    readonly stateSchema: z.ZodType<TState, TWireState>;
    /** Bidirectional creation-state superset; creation-only fields are never persisted. */
    readonly initialStateSchema?: z.ZodType<TInitialState, TWireInitialState>;
    defaultState(): TState;
    defaultInitialState?(): TInitialState;
    fromJSON(container: ReactWidgetContainer): TModel;
}

export interface LedgerEntry<TState> {
    id: WidgetId;
    type: WidgetType;
    location: WidgetLocation;
    windowId: WindowId;
    persistedState: TState;
}

// `any` is load-bearing: `SelfOwnedWidgetClass<unknown, ...>` rejects concrete
// widget classes because their `stateSchema` types are invariant.
export type AnySelfOwnedWidgetClass = SelfOwnedWidgetClass<any, SelfOwnedWidget<any>>;

/**
 * Decode untrusted persisted data in the schema's wire-to-runtime direction.
 * The input cast only selects the typed Zod direction; `decode` still validates
 * the value before returning it.
 */
export function decodeSelfOwnedWidgetState<TSchema extends z.ZodType>(
    schema: TSchema,
    value: unknown,
): z.output<TSchema> {
    return schema.decode(value as z.input<TSchema>);
}

/** Encode validated live model state into its persisted wire representation. */
export function encodeSelfOwnedWidgetState<TSchema extends z.ZodType>(
    schema: TSchema,
    value: z.output<TSchema>,
): z.input<TSchema> {
    return schema.encode(value);
}

export function getSelfOwnedWidgetInitialStateSchema(widgetClass: AnySelfOwnedWidgetClass): z.ZodType<any, any> {
    return widgetClass.initialStateSchema ?? widgetClass.stateSchema;
}
